import Btn from '@/components/dataDisplay/Btn';
import Tooltip from '@/components/dataDisplay/Tooltip';
import { stringifyQueryParameters } from '@/modules/core/i18n/i18nRouter';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  NextRouter,
  useRouter,
} from 'next/router';
import React, { Fragment } from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';
import usePreviewMode, { PreviewMode } from '../hooks/usePreviewMode';
import {
  startPreviewMode,
  stopPreviewMode,
} from '../previewMode';

type Props = {}

export const ExplanationTooltipOverlay: React.FunctionComponent = (): JSX.Element => {
  return (
    <Trans
      i18nKey={'previewModeBanner.previewModeTitleHelp'}
    >
      <span>
        L'environnement de prévisualisation vous permet de visualiser vos modifications de contenu. Cet environnement est synchronisé avec le back-office en temps réel.<br />
        Lorsque vous êtes satisfait.e de votre contenu, vous pouvez "Déployer en production" pour que les changements soient visibles pour vos utilisateurs finaux.
      </span>
    </Trans>
  );
};

/**
 * The behaviour of this component is completely different based on the stage the application is running within.
 *
 * Development: Display the banner and allow to enable/disable preview mode (for testing purposes)
 * Staging: Displays the banner that highlights the fact we're running inside a "preview environment"
 * Production: Doesn't display anything
 *
 * @param props
 */
const PreviewModeBanner: React.FunctionComponent<Props> = (props): JSX.Element => {
  const { isPreviewModeEnabled }: PreviewMode = usePreviewMode();
  const router: NextRouter = useRouter();
  const queryParameters: string = stringifyQueryParameters(router);
  const { t } = useTranslation();
  const {
    secondaryColor,
    secondaryColorVariant1,
  } = useTheme();

  if (process.env.NEXT_PUBLIC_APP_STAGE === 'production') {
    return null;
  }

  return (
    <header
      role="banner"
      css={css`
        display: inline-flex;
        justify-content: space-between;
        width: 100%;
        align-items: center;
        color: ${secondaryColor};
        background-color: ${secondaryColorVariant1};
        border: transparent;

        .left-actions-container,
        .explanations-container,
        .right-actions-container {
          width: calc(100vw / 3); // Each section takes the same width, dynamically calculated based on the available width
        }

        .explanations-container {
          text-align: center;

          .explanations-title {
            display: inline;
          }
        }

        .right-actions-container {
          display: flex;
          flex-direction: row-reverse; // Order elements from right to left

          .change-locale-container {
            display: inline-flex;
            align-items: center;
          }
        }

        [class*="fa-"] {
          margin-bottom: 1px
        }
      `}
    >
      {
        isPreviewModeEnabled ? (
          <Fragment>
            <div className={'left-actions-container'} />
            <div className={'explanations-container'}>
              {t(`previewModeBanner.previewModeEnabledTitle`, `Vous êtes sur l'environnement de prévisualisation`)}
              &nbsp;
              <Tooltip
                overlay={<ExplanationTooltipOverlay />}
                placement={'bottom'}
              >
                <FontAwesomeIcon icon={['fas', 'question-circle']} size={'xs'} />
              </Tooltip>
            </div>
            <div className={'right-actions-container'}>
              {
                process.env.NEXT_PUBLIC_APP_STAGE === 'development' && (
                  <Btn
                    mode={'secondary-reverse'}
                    color={'link'}
                    onClick={(): void => stopPreviewMode(queryParameters)}
                    onKeyPress={(): void => stopPreviewMode(queryParameters)}
                  >
                    {t(`previewModeBanner.stopPreviewMode`, `Stopper l'environnement de prévisualisation`)}
                  </Btn>
                )
              }
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className={'left-actions-container'} />
            <div className={'explanations-container'}>
              {t(`previewModeBanner.previewModeDisabledTitle`, `L'environnement de prévisualisation est désactivé`)}
              &nbsp;
              <Tooltip
                overlay={<ExplanationTooltipOverlay />}
                placement={'bottom'}
              >
                <FontAwesomeIcon icon={['fas', 'question-circle']} size={'xs'} />
              </Tooltip>
            </div>
            <div className={'right-actions-container'}>
              {
                process.env.NEXT_PUBLIC_APP_STAGE === 'development' && (
                  <Btn
                    mode={'secondary-reverse'}
                    color={'link'}
                    onClick={(): void => startPreviewMode(queryParameters)}
                    onKeyPress={(): void => startPreviewMode(queryParameters)}
                  >
                    {t(`previewModeBanner.startPreviewMode`, `Démarrer l'environnement de prévisualisation`)}
                  </Btn>
                )
              }
            </div>
          </Fragment>
        )
      }
    </header>
  );
};

export default PreviewModeBanner;
